/**
 * @module useIdleTimeout
 *
 * Tracks user inactivity and manages a three-phase idle timeout:
 *   ACTIVE → PROMPTED (warning) → IDLE (auto-logout)
 *
 * Features:
 *   - Throttled event listeners (mousemove 1s, others immediate)
 *   - Cross-tab synchronization via BroadcastChannel API
 *   - Visibility-aware: checks elapsed time when tab regains focus
 *   - Pause/resume for modals or critical operations
 *
 * Security context:
 *   Client-side idle detection is a UX layer. Server-side token
 *   expiry (Supabase JWT TTL) is the security boundary.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { IDLE_TIMEOUT_MS } from "../lib/sessionTimeout";

// ─── Types ───────────────────────────────────────────────────

type IdleState = "active" | "prompted" | "idle";

interface UseIdleTimeoutOptions {
  /** Milliseconds of inactivity before showing the warning prompt. Default: 6 hours */
  idleTimeout?: number;
  /** Milliseconds the prompt is shown before auto-logout. Default: 60 seconds */
  promptTimeout?: number;
  /** Called when transitioning to the PROMPTED state */
  onPrompt?: () => void;
  /** Called when transitioning to the IDLE state (timeout expired) */
  onIdle?: () => void;
  /** Called when user activity resets from PROMPTED back to ACTIVE */
  onActive?: () => void;
  /** Whether the timeout tracking is enabled. Set false on login/public pages. */
  enabled?: boolean;
}

interface UseIdleTimeoutReturn {
  /** Current state of the idle machine */
  state: IdleState;
  /** Whether the warning prompt should be visible */
  isPrompted: boolean;
  /** Whether the session has fully timed out */
  isIdle: boolean;
  /** Seconds remaining in the prompt countdown (0 when not prompted) */
  remainingSeconds: number;
  /** Manually reset to ACTIVE state (e.g. "Stay Signed In" button) */
  reset: () => void;
  /** Pause idle tracking (e.g. during file uploads) */
  pause: () => void;
  /** Resume idle tracking */
  resume: () => void;
}

// ─── Constants ───────────────────────────────────────────────

const DEFAULT_IDLE_TIMEOUT = IDLE_TIMEOUT_MS;
const DEFAULT_PROMPT_TIMEOUT = 60 * 1000; // 60 seconds
const THROTTLE_MS = 1000; // Throttle mousemove to 1 event/second
const CHANNEL_NAME = "seq-claims-idle-sync";

/** DOM events that indicate user activity */
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
];

// ─── Hook ────────────────────────────────────────────────────

export function useIdleTimeout(
  options: UseIdleTimeoutOptions = {}
): UseIdleTimeoutReturn {
  const {
    idleTimeout = DEFAULT_IDLE_TIMEOUT,
    promptTimeout = DEFAULT_PROMPT_TIMEOUT,
    onPrompt,
    onIdle,
    onActive,
    enabled = true,
  } = options;

  const [state, setState] = useState<IdleState>("active");
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Refs to avoid stale closures in event handlers
  const stateRef = useRef<IdleState>("active");
  // Initialize to 0, then set to Date.now() in mount effect to avoid impure render call
  const lastActivityRef = useRef<number>(0);
  const promptStartRef = useRef<number>(0);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pausedRef = useRef(false);
  const throttleRef = useRef<number>(0);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Stable callback refs
  const onPromptRef = useRef(onPrompt);
  const onIdleRef = useRef(onIdle);
  const onActiveRef = useRef(onActive);
  // Keep callback refs in sync via effect (avoids writing refs during render)
  useEffect(() => {
    onPromptRef.current = onPrompt;
    onIdleRef.current = onIdle;
    onActiveRef.current = onActive;
  }, [onPrompt, onIdle, onActive]);

  // Set initial activity timestamp in effect (avoids impure Date.now() during render)
  useEffect(() => {
    if (lastActivityRef.current === 0) {
      lastActivityRef.current = Date.now();
    }
  }, []);

  // ─── State Transitions ──────────────────────────────────

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const transitionToIdle = useCallback(() => {
    clearTimers();
    stateRef.current = "idle";
    setState("idle");
    setRemainingSeconds(0);
    onIdleRef.current?.();
  }, [clearTimers]);

  const startPromptCountdown = useCallback(() => {
    promptStartRef.current = Date.now();
    const totalSeconds = Math.ceil(promptTimeout / 1000);
    setRemainingSeconds(totalSeconds);

    // Update countdown every second
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - promptStartRef.current;
      const remaining = Math.max(
        0,
        Math.ceil((promptTimeout - elapsed) / 1000)
      );
      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        transitionToIdle();
      }
    }, 250); // 250ms for smooth countdown (updates 4x/sec, displays whole seconds)
  }, [promptTimeout, transitionToIdle]);

  const transitionToPrompted = useCallback(() => {
    clearTimers();
    stateRef.current = "prompted";
    setState("prompted");
    onPromptRef.current?.();
    startPromptCountdown();
  }, [clearTimers, startPromptCountdown]);

  const startIdleTimer = useCallback(() => {
    clearTimers();
    idleTimerRef.current = setTimeout(() => {
      transitionToPrompted();
    }, idleTimeout);
  }, [idleTimeout, clearTimers, transitionToPrompted]);

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    clearTimers();
    stateRef.current = "active";
    setState("active");
    setRemainingSeconds(0);

    if (stateRef.current !== "active") {
      onActiveRef.current?.();
    }

    if (!pausedRef.current) {
      startIdleTimer();
    }

    // Broadcast reset to other tabs
    try {
      channelRef.current?.postMessage({
        type: "activity",
        timestamp: Date.now(),
      });
    } catch {
      // BroadcastChannel may be closed
    }
  }, [clearTimers, startIdleTimer]);

  const pause = useCallback(() => {
    pausedRef.current = true;
    clearTimers();
  }, [clearTimers]);

  const resume = useCallback(() => {
    pausedRef.current = false;
    if (stateRef.current === "active") {
      startIdleTimer();
    }
  }, [startIdleTimer]);

  // ─── Activity Handler ───────────────────────────────────

  const handleActivity = useCallback(() => {
    if (pausedRef.current || !enabled) return;

    // Only reset during ACTIVE state. During PROMPTED, the user
    // must explicitly click "Stay Signed In" to reset.
    if (stateRef.current !== "active") return;

    const now = Date.now();

    // Throttle to prevent performance impact from high-frequency events
    if (now - throttleRef.current < THROTTLE_MS) return;
    throttleRef.current = now;

    lastActivityRef.current = now;

    // Restart the idle timer
    clearTimers();
    startIdleTimer();

    // Broadcast to other tabs
    try {
      channelRef.current?.postMessage({
        type: "activity",
        timestamp: now,
      });
    } catch {
      // Channel may be closed
    }
  }, [enabled, clearTimers, startIdleTimer]);

  // ─── Visibility Change Handler ──────────────────────────

  const handleVisibilityChange = useCallback(() => {
    if (!enabled || pausedRef.current) return;

    if (document.visibilityState === "visible") {
      const elapsed = Date.now() - lastActivityRef.current;

      if (stateRef.current === "active" && elapsed >= idleTimeout) {
        // Tab was hidden long enough to trigger prompt
        transitionToPrompted();
      } else if (stateRef.current === "prompted") {
        // Check if prompt has also expired while tab was hidden
        const promptElapsed = Date.now() - promptStartRef.current;
        if (promptElapsed >= promptTimeout) {
          transitionToIdle();
        }
      } else if (stateRef.current === "active") {
        // Restart timer with remaining time
        clearTimers();
        const remaining = idleTimeout - elapsed;
        idleTimerRef.current = setTimeout(() => {
          transitionToPrompted();
        }, remaining);
      }
    }
  }, [
    enabled,
    idleTimeout,
    promptTimeout,
    clearTimers,
    transitionToPrompted,
    transitionToIdle,
  ]);

  // ─── Cross-Tab Synchronization ──────────────────────────

  useEffect(() => {
    if (!enabled) return;

    // Not all browsers support BroadcastChannel; fail gracefully
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = channel;

      channel.onmessage = (event) => {
        const { type, timestamp } = event.data;

        if (type === "activity" && stateRef.current === "active") {
          // Another tab reported activity, reset our timer
          lastActivityRef.current = timestamp;
          clearTimers();
          startIdleTimer();
        } else if (type === "logout") {
          // Another tab initiated logout, follow suit
          transitionToIdle();
        }
      };

      return () => {
        channel.close();
        channelRef.current = null;
      };
    } catch {
      // BroadcastChannel not supported (e.g. some Safari versions)
      // Single-tab behavior only
      return;
    }
  }, [enabled, clearTimers, startIdleTimer, transitionToIdle]);

  // ─── Event Listeners ────────────────────────────────────

  useEffect(() => {
    if (!enabled) return;

    // Start the idle timer
    startIdleTimer();

    // Bind activity events
    const options: AddEventListenerOptions = { passive: true, capture: true };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, options);
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity, options);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, startIdleTimer, handleActivity, handleVisibilityChange, clearTimers]);

  // ─── Cleanup on unmount ─────────────────────────────────

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    state,
    isPrompted: state === "prompted",
    isIdle: state === "idle",
    remainingSeconds,
    reset,
    pause,
    resume,
  };
}
